import {
    Component,
    DoCheck,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { GraphsService } from '@service/graphs/graphs.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType, getAnalogTypesSet } from 'icreate/common/models/channel-type';
import {
    Graph,
    GaugeGraph,
    GaugeGraphChannel,
    MAX_GAUGE_GRAPH_CHANNELS,
    getDefaultScaleDivision,
} from 'icreate/common/models/graphs';
import _ from 'lodash';
import { Observable, Subscription, lastValueFrom, take } from 'rxjs';

@Component({
    selector: 'app-gauge-graph-table',
    templateUrl: './gauge-graph-table.component.html',
    styleUrl: './gauge-graph-table.component.css',
    standalone: false,
})
export class GaugeGraphTableComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
    @Input() graph: Graph;
    @Input() closeEvent: Observable<void>;
    @Input() saveEvent: Observable<void>;
    @Output() onUpdateSave: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onCloseGraph: EventEmitter<void> = new EventEmitter<void>();
    @Output() saveCompleted = new EventEmitter<void>();
    @ViewChild('cancelConfirmation') cancelConfirmation: ConfirmationWindowComponent;

    gaugeGraph: GaugeGraph;
    initialGraph: GaugeGraph;
    allowedTypesSet: Set<ChannelType> = getAnalogTypesSet();

    saveIsEnabled: boolean = false;

    closeSubscription: Subscription;
    saveSubscription: Subscription;

    maxChannelSlots = MAX_GAUGE_GRAPH_CHANNELS;
    selectedRowIndex: number;

    constructor(
        private graphsService: GraphsService,
        private dialog: MatDialog
    ) {}

    async ngOnInit() {
        await this.loadGraph();
        this.closeSubscription = this.closeEvent.subscribe(async () => await this.cancel());
        this.saveSubscription = this.saveEvent.subscribe(async () => await this.save());
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (this.graph && changes.graph && !changes.graph.firstChange) {
            await this.loadGraph();
        }
    }

    ngDoCheck(): void {
        const isEqual = _.isEqual(this.initialGraph, this.gaugeGraph);
        if (isEqual && this.saveIsEnabled) {
            this.onUpdateSave.emit(false);
        } else if (!isEqual && !this.saveIsEnabled) {
            this.onUpdateSave.emit(true);
        } else if (isEqual && !this.saveIsEnabled) {
            this.onUpdateSave.emit(false);
        }
    }

    ngOnDestroy(): void {
        this.closeSubscription?.unsubscribe();
        this.saveSubscription?.unsubscribe();
    }

    async loadGraph() {
        this.graphsService.showLoadingSpinner(true);
        this.gaugeGraph = await this.graphsService.getGraphById(this.graph.id, this.graph.type);
        if (!this.gaugeGraph) {
            this.gaugeGraph = new GaugeGraph();
            this.gaugeGraph.graphId = this.graph.id;
            this.gaugeGraph.channels = [];
        } else if (this.gaugeGraph.channels) {
            this.gaugeGraph.channels = this.gaugeGraph.channels?.length
                ? this.gaugeGraph.channels
                      .sort((a: any, b: any) => a.rowNumber - b.rowNumber)
                      .map(({ channel, scaleDivision }) => ({ channel, scaleDivision }))
                : [];
        } else {
            this.gaugeGraph.channels = [];
        }
        this.initialGraph = _.cloneDeep(this.gaugeGraph);
        this.graphsService.showLoadingSpinner(false);
    }

    showAddNewChannel() {
        return !this.gaugeGraph.channels || this.gaugeGraph.channels.length < MAX_GAUGE_GRAPH_CHANNELS;
    }

    async addChannelRow() {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.graph.vesselId,
                assignedChannel: undefined,
                allowedTypesSet: this.allowedTypesSet,
                setShouldDisableTypes: true,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
        if (channelSelected) {
            const newGraphChannel: GaugeGraphChannel = {
                channel: channelSelected,
                scaleDivision: getDefaultScaleDivision(channelSelected),
            };
            this.gaugeGraph.channels.push(newGraphChannel);
        }
    }

    deleteChannelRow(index) {
        this.gaugeGraph.channels.splice(index, 1);
    }

    channelRowUpdated(channel, index) {
        this.gaugeGraph.channels[index] = channel;
    }

    async cancel() {
        if (!_.isEqual(this.initialGraph, this.gaugeGraph)) {
            this.cancelConfirmation.setVisible(true);
            let confirmationOption = await lastValueFrom(this.cancelConfirmation.onOptionSelected.pipe(take(1)));
            if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                this.save();
            } else if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                return;
            }
            this.onCloseGraph.emit();
        } else {
            this.onCloseGraph.emit();
        }
    }

    async save() {
        if (_.isEqual(this.initialGraph, this.gaugeGraph)) return;
        try {
            this.graphsService.showLoadingSpinner(true);
            await this.graphsService.updateGaugeGraph(this.gaugeGraph);
            this.initialGraph = _.cloneDeep(this.gaugeGraph);
            this.onUpdateSave.emit(false);
            this.graphsService.showLoadingSpinner(false);
            this.saveCompleted.emit();
        } catch (err) {
            console.error(err);
            this.graphsService.showLoadingSpinner(false);
        }
    }

    optionsToggled(index: number): void {
        if (this.selectedRowIndex == index) {
            this.selectedRowIndex = null;
        } else {
            this.selectedRowIndex = index;
        }
    }
}
